/**
 * Who asked for a scan, as a badge. Shared by every application's history table
 * so the Source column reads the same everywhere.
 *
 * `stress` and `benchmark` both come from a QA load test and are split by whether
 * the run paid for its generations: a stress run's images are noise produced at
 * zero spend, while a benchmark run went to the real providers. Worth telling
 * apart at a glance — the first explains a burst of traffic that cost nothing and
 * whose output means nothing, the second explains a bill.
 *
 * Mirrors `shared.job_source`.
 */

const DEFAULT_BASE = 'px-2.5 py-1 rounded-full text-xs font-medium';

interface Variant {
  label: string;
  classes: string;
  title: string;
}

const VARIANTS: Record<string, Variant> = {
  manual: {
    label: 'Manual',
    classes: 'bg-cyan-50 text-cyan-600',
    title: 'Submitted by hand from this application',
  },
  uvcamp: {
    label: 'UVCamp',
    classes: 'bg-indigo-50 text-indigo-600',
    title: 'Requested through the API by UVCAMP or an integration',
  },
  stress: {
    label: 'Stress',
    classes: 'bg-amber-50 text-amber-700',
    title: 'QA load test in dummy mode: real queueing, noise images, no spend',
  },
  benchmark: {
    label: 'Benchmark',
    classes: 'bg-violet-50 text-violet-700',
    title: 'QA load test against the real providers: this one cost money',
  },
};

export function SourceBadge({
  source,
  baseClassName = DEFAULT_BASE,
}: {
  source?: string | null;
  baseClassName?: string;
}) {
  if (!source) return <span className="text-slate-400">-</span>;
  const variant = VARIANTS[source];
  if (!variant) {
    return <span className={`${baseClassName} bg-slate-100 text-slate-600`}>{source}</span>;
  }
  return (
    <span className={`${baseClassName} ${variant.classes}`} title={variant.title}>
      {variant.label}
    </span>
  );
}
